<template>
  <div class="space-y-6">
    <!-- Success Message -->
    <div
      v-if="showSuccessMessage"
      class="p-4 bg-green-50 border border-green-200 rounded-lg"
    >
      <div class="flex">
        <div class="flex-shrink-0">
          <svg
            class="h-5 w-5 text-green-400"
            viewBox="0 0 20 20"
            fill="currentColor"
          >
            <path
              fill-rule="evenodd"
              d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
              clip-rule="evenodd"
            />
          </svg>
        </div>
        <div class="ml-3">
          <p class="text-sm text-green-800">{{ successMessage }}</p>
        </div>
        <div class="ml-auto pl-3">
          <button
            @click="showSuccessMessage = false"
            class="inline-flex bg-green-50 rounded-md p-1.5 text-green-500 hover:bg-green-100 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-offset-green-50 focus:ring-green-600"
          >
            <svg class="h-3 w-3" viewBox="0 0 20 20" fill="currentColor">
              <path
                fill-rule="evenodd"
                d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z"
                clip-rule="evenodd"
              />
            </svg>
          </button>
        </div>
      </div>
    </div>

    <!-- Error Message -->
    <div
      v-if="showErrorMessage"
      class="p-4 bg-red-50 border border-red-200 rounded-lg"
    >
      <div class="flex">
        <div class="flex-shrink-0">
          <svg
            class="h-5 w-5 text-red-400"
            viewBox="0 0 20 20"
            fill="currentColor"
          >
            <path
              fill-rule="evenodd"
              d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z"
              clip-rule="evenodd"
            />
          </svg>
        </div>
        <div class="ml-3">
          <p class="text-sm text-red-800">{{ errorMessage }}</p>
        </div>
        <div class="ml-auto pl-3">
          <button
            @click="showErrorMessage = false"
            class="inline-flex bg-red-50 rounded-md p-1.5 text-red-500 hover:bg-red-100 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-offset-red-50 focus:ring-red-600"
          >
            <svg class="h-3 w-3" viewBox="0 0 20 20" fill="currentColor">
              <path
                fill-rule="evenodd"
                d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z"
                clip-rule="evenodd"
              />
            </svg>
          </button>
        </div>
      </div>
    </div>

    <!-- Loading State -->
    <div v-if="isLoading" class="text-center py-12">
      <div
        class="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto"
      ></div>
      <p class="mt-4 text-sm text-gray-600">Loading settings...</p>
    </div>

    <!-- Settings Content -->
    <div v-else>
      <!-- Tab Navigation -->
      <div class="border-b border-gray-200 mb-6">
        <nav class="-mb-px flex space-x-8" aria-label="Tabs">
          <button
            v-if="!isResident"
            @click="activeTab = 'hoa'"
            :class="[
              activeTab === 'hoa'
                ? 'border-primary text-primary'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300',
              'whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm transition-colors duration-200',
            ]"
          >
            HOA
          </button>
          <button
            @click="activeTab = 'user'"
            :class="[
              activeTab === 'user'
                ? 'border-primary text-primary'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300',
              'whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm transition-colors duration-200',
            ]"
          >
            User
          </button>
          <button
            v-if="!isResident"
            @click="activeTab = 'localization'"
            :class="[
              activeTab === 'localization'
                ? 'border-primary text-primary'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300',
              'whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm transition-colors duration-200',
            ]"
          >
            Localization
          </button>
          <button
            v-if="!isResident"
            @click="activeTab = 'payments'"
            :class="[
              activeTab === 'payments'
                ? 'border-primary text-primary'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300',
              'whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm transition-colors duration-200',
            ]"
          >
            Payments
          </button>
          <button
            v-if="!isResident"
            @click="activeTab = 'bank'"
            :class="[
              activeTab === 'bank'
                ? 'border-primary text-primary'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300',
              'whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm transition-colors duration-200',
            ]"
          >
            Bank
          </button>
          <button
            v-if="!isResident"
            @click="activeTab = 'reminders'"
            :class="[
              activeTab === 'reminders'
                ? 'border-primary text-primary'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300',
              'whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm transition-colors duration-200',
            ]"
          >
            Reminders
          </button>
        </nav>
      </div>

      <!-- Tab Content -->
      <div class="card-modern">
        <!-- HOA Tab (hidden from residents; they cannot see or update community settings) -->
        <div v-if="activeTab === 'hoa' && !isResident" class="space-y-6">
          <h2 class="text-base font-semibold text-gray-900">
            Community Settings
          </h2>
          <div class="space-y-4">
            <div>
              <label class="block text-sm font-medium text-gray-700 mb-2"
                >Community Name</label
              >
              <input
                v-model="hoaForm.name"
                type="text"
                class="input-field"
                placeholder="Enter community name"
              />
            </div>
            <div>
              <label class="block text-sm font-medium text-gray-700 mb-2"
                >Address</label
              >
              <input
                id="hoa-address"
                v-model="hoaForm.address"
                type="text"
                class="input-field"
                autocomplete="off"
                placeholder="Start typing address..."
              />
            </div>
            <div>
              <label class="block text-sm font-medium text-gray-700 mb-2"
                >Phone Number</label
              >
              <input
                v-model="hoaForm.phone"
                type="tel"
                class="input-field"
                placeholder="Enter phone number"
              />
            </div>

            <!-- Community logo (optional) -->
            <div>
              <label class="block text-sm font-medium text-gray-700 mb-2"
                >Community logo (optional)</label
              >
              <p class="text-xs text-gray-500 mb-2">
                Shown in the header, on invoices, and in PDFs. PNG or JPG, max
                2MB.
              </p>
              <div v-if="hoaLogoUrl" class="flex flex-col gap-2">
                <img
                  :src="hoaLogoUrl"
                  alt="Community logo"
                  class="max-h-20 w-auto object-contain border border-gray-200 rounded-lg bg-white"
                />
                <div class="flex gap-2">
                  <input
                    ref="hoaLogoInputRef"
                    type="file"
                    accept="image/png,image/jpeg,image/jpg"
                    class="hidden"
                    @change="onHoaLogoFileChange"
                  />
                  <button
                    type="button"
                    @click="triggerHoaLogoInput"
                    :disabled="isUploadingHoaLogo"
                    class="btn-secondary text-sm"
                  >
                    <span v-if="isUploadingHoaLogo">Uploading...</span>
                    <span v-else>Replace logo</span>
                  </button>
                  <button
                    type="button"
                    @click="removeHoaLogo"
                    :disabled="isRemovingHoaLogo"
                    class="btn-secondary text-sm text-red-600 hover:text-red-700 hover:bg-red-50 border-red-200"
                  >
                    <span v-if="isRemovingHoaLogo">Removing...</span>
                    <span v-else>Remove logo</span>
                  </button>
                </div>
              </div>
              <div v-else class="flex flex-col gap-2">
                <input
                  ref="hoaLogoInputRef"
                  type="file"
                  accept="image/png,image/jpeg,image/jpg"
                  class="hidden"
                  @change="onHoaLogoFileChange"
                />
                <button
                  type="button"
                  @click="triggerHoaLogoInput"
                  :disabled="isUploadingHoaLogo"
                  class="btn-secondary text-sm w-fit"
                >
                  <span v-if="isUploadingHoaLogo">Uploading...</span>
                  <span v-else>Add logo</span>
                </button>
              </div>
            </div>
          </div>
          <div
            v-if="!isResident"
            class="flex justify-end pt-4 border-t border-gray-200"
          >
            <button
              @click="saveHoaSettings"
              :disabled="isSavingHoa"
              class="btn-primary"
            >
              <span v-if="isSavingHoa">Saving...</span>
              <span v-else>Save Changes</span>
            </button>
          </div>
        </div>

        <!-- User Tab -->
        <div v-if="activeTab === 'user'" class="space-y-6">
          <h2 class="text-base font-semibold text-gray-900">User Settings</h2>
          <div class="space-y-4">
            <div>
              <label class="block text-sm font-medium text-gray-700 mb-2"
                >Full Name</label
              >
              <input
                v-model="userForm.name"
                type="text"
                class="input-field"
                placeholder="Enter full name"
              />
            </div>
            <div>
              <label class="block text-sm font-medium text-gray-700 mb-2"
                >Email Address</label
              >
              <input
                v-model="userForm.email"
                type="email"
                class="input-field"
                placeholder="Enter email address"
              />
            </div>
            <div>
              <label class="block text-sm font-medium text-gray-700 mb-2"
                >Phone Number</label
              >
              <input
                v-model="userForm.phone_number"
                type="tel"
                class="input-field"
                placeholder="Enter phone number"
              />
            </div>
          </div>
          <div class="flex justify-end pt-4 border-t border-gray-200">
            <button
              @click="saveUserSettings"
              :disabled="isSavingUser"
              class="btn-primary"
            >
              <span v-if="isSavingUser">Saving...</span>
              <span v-else>Save Changes</span>
            </button>
          </div>
        </div>

        <!-- Localization Tab -->
        <div v-if="activeTab === 'localization'" class="space-y-6">
          <h2 class="text-base font-semibold text-gray-900">
            Localization Settings
          </h2>
          <div class="space-y-4">
            <div>
              <label class="block text-sm font-medium text-gray-700 mb-2"
                >Currency</label
              >
              <select v-model="localizationForm.currency" class="input-field">
                <option value="USD">USD</option>
                <option value="INR">INR</option>
              </select>
            </div>
            <div>
              <label class="block text-sm font-medium text-gray-700 mb-2"
                >Currency Format</label
              >
              <input
                v-model="localizationForm.currency_format"
                type="text"
                class="input-field"
                placeholder="e.g., $#,##0.00 or ₹#,##0.00"
              />
              <p class="mt-1 text-xs text-gray-500">
                Examples: $#,##0.00, ₹#,##0.00
              </p>
            </div>
            <div>
              <label class="block text-sm font-medium text-gray-700 mb-2"
                >Timezone</label
              >
              <select v-model="localizationForm.timezone" class="input-field">
                <option value="UTC">UTC</option>
                <option value="America/New_York">America/New_York (EST)</option>
                <option value="America/Chicago">America/Chicago (CST)</option>
                <option value="America/Denver">America/Denver (MST)</option>
                <option value="America/Los_Angeles">
                  America/Los_Angeles (PST)
                </option>
                <option value="Asia/Kolkata">Asia/Kolkata (IST)</option>
                <option value="Europe/London">Europe/London (GMT)</option>
                <option value="Europe/Paris">Europe/Paris (CET)</option>
              </select>
            </div>
            <div>
              <label class="block text-sm font-medium text-gray-700 mb-2"
                >Date Format</label
              >
              <select
                v-model="localizationForm.date_format"
                class="input-field"
              >
                <option value="MM/DD/YYYY">MM/DD/YYYY</option>
                <option value="DD/MM/YYYY">DD/MM/YYYY</option>
                <option value="YYYY-MM-DD">YYYY-MM-DD</option>
              </select>
            </div>
            <div>
              <label class="block text-sm font-medium text-gray-700 mb-2"
                >First Month of Year</label
              >
              <select
                v-model="localizationForm.first_month_of_year"
                class="input-field"
              >
                <option v-for="month in months" :key="month" :value="month">
                  {{ month }}
                </option>
              </select>
            </div>
          </div>
          <div class="flex justify-end pt-4 border-t border-gray-200">
            <button
              @click="saveLocalizationSettings"
              :disabled="isSavingLocalization"
              class="btn-primary"
            >
              <span v-if="isSavingLocalization">Saving...</span>
              <span v-else>Save Changes</span>
            </button>
          </div>
        </div>

        <!-- Payments Tab -->
        <div v-if="activeTab === 'payments'" class="space-y-6">
          <h2 class="text-base font-semibold text-gray-900">
            Payment Settings
          </h2>
          <div class="space-y-4">
            <div class="p-4 bg-blue-50 border border-blue-200 rounded-lg">
              <h3 class="font-medium text-blue-900 mb-2">Stripe Connect</h3>
              <p class="text-sm text-blue-800 mb-4">
                Connect your Stripe account to accept online payments from
                residents.
              </p>

              <!-- Not Connected State -->
              <div v-if="!stripeConnectId" class="space-y-4">
                <p class="text-sm text-gray-600">
                  Status:
                  <span class="font-semibold text-red-600">Not Connected</span>
                </p>
                <button
                  @click="connectStripe"
                  :disabled="isConnectingStripe"
                  class="btn-primary"
                >
                  <span v-if="isConnectingStripe">Connecting...</span>
                  <span v-else>Connect Stripe Account</span>
                </button>
              </div>

              <!-- Connected State -->
              <div v-else class="space-y-4">
                <div class="space-y-2">
                  <p class="text-sm text-gray-600">
                    Status:
                    <span
                      :class="[
                        'font-semibold',
                        stripeConnectStatus === 'active'
                          ? 'text-green-600'
                          : 'text-yellow-600',
                      ]"
                    >
                      {{
                        stripeConnectStatus === 'active' ? 'Active' : 'Pending'
                      }}
                    </span>
                  </p>
                  <p class="text-xs text-gray-500">
                    Account ID: {{ stripeConnectId }}
                  </p>
                </div>

                <!-- Pending Details -->
                <div
                  v-if="stripeConnectStatus !== 'active'"
                  class="p-3 bg-yellow-50 border border-yellow-200 rounded"
                >
                  <p class="text-sm text-yellow-800">
                    {{
                      !chargesEnabled
                        ? 'Your account is being reviewed. Payments cannot be processed yet.'
                        : 'Please complete your account setup.'
                    }}
                  </p>
                </div>

                <!-- Active Details -->
                <div
                  v-else
                  class="space-y-2 p-3 bg-green-50 border border-green-200 rounded"
                >
                  <p class="text-sm text-green-800 font-medium">
                    ✓ Your account is ready to accept payments
                  </p>
                  <p class="text-xs text-green-700">
                    Platform fee: 1% of each transaction
                  </p>
                </div>

                <div class="flex gap-2 pt-2 flex-wrap">
                  <button
                    @click="openStripeDashboard"
                    :disabled="isLoadingDashboard"
                    class="btn-primary flex-1 min-w-[120px]"
                  >
                    <span v-if="isLoadingDashboard">Loading...</span>
                    <span v-else>Open Dashboard</span>
                  </button>
                  <button
                    @click="verifyStripeStatus"
                    :disabled="isVerifyingStatus"
                    class="btn-secondary flex-1 min-w-[120px]"
                  >
                    <span v-if="isVerifyingStatus">Checking...</span>
                    <span v-else>Refresh Status</span>
                  </button>
                  <button
                    @click="showDisconnectConfirmation = true"
                    :disabled="isDisconnecting"
                    class="btn-secondary flex-1 min-w-[120px] text-red-600 hover:text-red-700 hover:bg-red-50 border-red-200"
                  >
                    <span v-if="isDisconnecting">Disconnecting...</span>
                    <span v-else>Disconnect</span>
                  </button>
                </div>
              </div>
            </div>

            <!-- Zelle Section -->
            <div class="p-4 bg-gray-50 border border-gray-200 rounded-lg">
              <h3 class="font-medium text-gray-900 mb-2">Zelle</h3>
              <p class="text-sm text-gray-600 mb-4">
                Allow residents to pay HOA dues via Zelle. They will send
                payment to the email or phone below; you record and approve
                payments when received.
              </p>
              <div class="space-y-4">
                <label class="flex items-center gap-2 cursor-pointer">
                  <input
                    v-model="zelleForm.zelle_enabled"
                    type="checkbox"
                    class="h-4 w-4 text-indigo-600 rounded border-gray-300 focus:ring-indigo-500"
                  />
                  <span class="text-sm font-medium text-gray-700"
                    >Enable Zelle payments</span
                  >
                </label>
                <div v-if="zelleForm.zelle_enabled" class="space-y-4 pl-0">
                  <div>
                    <label class="block text-sm font-medium text-gray-700 mb-1"
                      >Zelle email</label
                    >
                    <input
                      v-model="zelleForm.zelle_email"
                      type="email"
                      class="input-field"
                      placeholder="e.g. payments@yourhoa.com"
                    />
                  </div>
                  <div>
                    <label class="block text-sm font-medium text-gray-700 mb-1"
                      >Zelle phone</label
                    >
                    <input
                      v-model="zelleForm.zelle_phone"
                      type="tel"
                      class="input-field"
                      placeholder="e.g. (555) 123-4567"
                    />
                  </div>
                  <p class="text-xs text-gray-500">
                    Provide at least one so residents know where to send
                    payment.
                  </p>

                  <!-- QR code -->
                  <div>
                    <label class="block text-sm font-medium text-gray-700 mb-1"
                      >QR code (optional)</label
                    >
                    <p class="text-xs text-gray-500 mb-2">
                      Upload a QR code image residents can scan (e.g. from your
                      bank’s Zelle page).
                    </p>
                    <div v-if="zelleQrUrl" class="flex flex-col gap-2">
                      <img
                        :src="zelleQrUrl"
                        alt="Zelle QR code"
                        class="w-[360px] h-[360px] min-w-[360px] min-h-[360px] object-contain border border-gray-200 rounded-lg bg-white"
                      />
                      <p class="text-sm text-gray-600">
                        Scan this QR code with your phone to verify it works
                        before asking residents to use it.
                      </p>
                      <div class="flex gap-2">
                        <input
                          ref="zelleQrInputRef"
                          type="file"
                          accept="image/png,image/jpeg,image/jpg"
                          class="hidden"
                          @change="onZelleQrFileChange"
                        />
                        <button
                          type="button"
                          @click="triggerZelleQrInput"
                          :disabled="isUploadingZelleQr"
                          class="btn-secondary text-sm"
                        >
                          <span v-if="isUploadingZelleQr">Uploading...</span>
                          <span v-else>Replace QR</span>
                        </button>
                        <button
                          type="button"
                          @click="removeZelleQr"
                          :disabled="isRemovingZelleQr"
                          class="btn-secondary text-sm text-red-600 hover:text-red-700 hover:bg-red-50 border-red-200"
                        >
                          <span v-if="isRemovingZelleQr">Removing...</span>
                          <span v-else>Remove QR code</span>
                        </button>
                      </div>
                    </div>
                    <div v-else class="flex flex-col gap-2">
                      <input
                        ref="zelleQrInputRef"
                        type="file"
                        accept="image/png,image/jpeg,image/jpg"
                        class="hidden"
                        @change="onZelleQrFileChange"
                      />
                      <button
                        type="button"
                        @click="triggerZelleQrInput"
                        :disabled="isUploadingZelleQr"
                        class="btn-secondary text-sm w-fit"
                      >
                        <span v-if="isUploadingZelleQr">Uploading...</span>
                        <span v-else>Upload QR code</span>
                      </button>
                    </div>
                    <!-- Instructions under QR -->
                    <div class="mt-3">
                      <label
                        class="block text-sm font-medium text-gray-700 mb-1"
                      >
                        Instructions for residents (when they scan the QR code)
                      </label>
                      <textarea
                        v-model="zelleForm.zelle_instructions"
                        rows="4"
                        class="input-field"
                        placeholder='e.g. Open Zelle, scan this code, enter the amount shown, and confirm. Then tap "I&apos;ve sent the payment" below.'
                      />
                    </div>
                  </div>
                </div>
                <div class="flex justify-end pt-4 border-t border-gray-200">
                  <button
                    @click="saveZelleSettings"
                    :disabled="isSavingZelle"
                    class="btn-primary"
                  >
                    <span v-if="isSavingZelle">Saving...</span>
                    <span v-else>Save Zelle settings</span>
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- Bank Tab -->
        <div v-if="activeTab === 'bank'" class="space-y-6">
          <h2 class="text-base font-semibold text-gray-900">
            Bank Account Integration
          </h2>
          <p class="text-sm text-gray-600">
            Connect your bank account to view and reconcile transactions.
          </p>

          <!-- Connected Bank Accounts -->
          <div v-if="bankAccounts.length > 0" class="space-y-4">
            <h3 class="text-sm font-medium text-gray-900">
              Connected Accounts
            </h3>
            <div class="space-y-3">
              <div
                v-for="account in bankAccounts"
                :key="account.id"
                class="p-4 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors"
              >
                <div class="flex items-start justify-between">
                  <div class="flex-1">
                    <p class="font-medium text-gray-900">
                      {{ account.account_name }}
                    </p>
                    <p class="text-sm text-gray-600 mt-1">
                      {{ account.institution_name }} • ••••{{
                        account.account_mask
                      }}
                    </p>
                    <div
                      class="mt-2 flex items-center gap-4 text-xs text-gray-500"
                    >
                      <span
                        :class="[
                          'px-2 py-1 rounded',
                          account.status === 'active'
                            ? 'bg-green-50 text-green-700'
                            : 'bg-yellow-50 text-yellow-700',
                        ]"
                      >
                        {{
                          account.status === 'active' ? 'Connected' : 'Error'
                        }}
                      </span>
                      <span v-if="account.last_synced_at">
                        Last synced: {{ formatDate(account.last_synced_at) }}
                      </span>
                    </div>
                  </div>
                  <button
                    @click="openDisconnectBankModal(account)"
                    :disabled="isDisconnectingBank"
                    class="ml-4 px-3 py-2 text-sm text-red-600 hover:bg-red-50 rounded transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    Disconnect
                  </button>
                </div>
              </div>
            </div>
          </div>

          <!-- No Accounts State -->
          <div
            v-else
            class="p-4 bg-gray-50 border border-gray-200 rounded-lg text-center"
          >
            <svg
              class="mx-auto h-12 w-12 text-gray-400 mb-3"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                stroke-linecap="round"
                stroke-linejoin="round"
                stroke-width="2"
                d="M3 10h18M3 14h18m-9-4v8m-7 0h14a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z"
              />
            </svg>
            <p class="text-gray-600 mb-4">No bank accounts connected yet</p>
            <button
              @click="connectBank"
              :disabled="isConnectingBank"
              class="btn-primary"
            >
              <span v-if="isConnectingBank">Connecting...</span>
              <span v-else>Connect Bank Account</span>
            </button>
          </div>

          <!-- Connect New Account Button -->
          <div
            v-if="bankAccounts.length > 0"
            class="pt-4 border-t border-gray-200"
          >
            <button
              @click="connectBank"
              :disabled="isConnectingBank"
              class="btn-primary"
            >
              <span v-if="isConnectingBank">Connecting...</span>
              <span v-else>+ Add Another Bank Account</span>
            </button>
          </div>

          <!-- Transaction Sync Settings -->
          <div
            v-if="bankAccounts.length > 0"
            class="p-4 bg-blue-50 border border-blue-200 rounded-lg"
          >
            <h3 class="font-medium text-blue-900 mb-2">Transaction Sync</h3>
            <p class="text-sm text-blue-800 mb-4">
              Transactions are automatically synced every hour. You can also
              manually refresh anytime.
            </p>
            <button
              @click="manualSyncBankAccounts"
              :disabled="isManualSyncing"
              class="btn-secondary"
            >
              <span v-if="isManualSyncing">Syncing...</span>
              <span v-else>Refresh Transactions Now</span>
            </button>
          </div>
        </div>

        <!-- Reminders Tab -->
        <div v-if="activeTab === 'reminders'" class="space-y-6">
          <h2 class="text-base font-semibold text-gray-900">
            Reminder Settings
          </h2>
          <p class="text-sm text-gray-600">
            Configure invoice reminder timing. Reminder scheduling uses your
            selected timezone from Localization settings.
          </p>

          <div
            class="p-4 bg-blue-50 border border-blue-200 rounded-lg space-y-4"
          >
            <h3 class="font-medium text-blue-900">Invoice Due Reminders</h3>
            <label class="flex items-center gap-2 cursor-pointer">
              <input
                v-model="remindersForm.invoice_due.enabled"
                type="checkbox"
                class="h-4 w-4 text-indigo-600 rounded border-gray-300 focus:ring-indigo-500"
              />
              <span class="text-sm font-medium text-gray-700"
                >Enable invoice due reminders</span
              >
            </label>

            <div
              :class="[
                'space-y-4',
                remindersForm.invoice_due.enabled ? '' : 'opacity-60',
              ]"
            >
              <div>
                <label class="block text-sm font-medium text-gray-700 mb-2">
                  Pre-due offsets (days)
                </label>
                <p class="text-xs text-gray-500 mb-3">
                  A reminder is sent when an invoice is exactly this many days
                  before due date.
                </p>
                <div class="space-y-2">
                  <div
                    v-for="(offset, index) in remindersForm.invoice_due
                      .pre_due_offsets_days"
                    :key="`offset-${index}`"
                    class="flex items-center gap-2"
                  >
                    <input
                      v-model.number="
                        remindersForm.invoice_due.pre_due_offsets_days[index]
                      "
                      type="number"
                      min="1"
                      class="input-field w-40"
                    />
                    <button
                      type="button"
                      class="btn-secondary text-sm text-red-600 hover:text-red-700 hover:bg-red-50 border-red-200"
                      @click="removePreDueOffset(index)"
                    >
                      Remove
                    </button>
                  </div>
                </div>
                <div class="flex gap-2 mt-3">
                  <button
                    type="button"
                    class="btn-secondary text-sm"
                    @click="addPreDueOffset"
                  >
                    Add offset
                  </button>
                  <button
                    type="button"
                    class="btn-secondary text-sm"
                    @click="
                      remindersForm.invoice_due.pre_due_offsets_days = [
                        30, 15, 7, 3, 2, 1,
                      ]
                    "
                  >
                    Reset defaults
                  </button>
                </div>
              </div>

              <div>
                <label class="block text-sm font-medium text-gray-700 mb-2">
                  Post-due interval (days)
                </label>
                <input
                  v-model.number="
                    remindersForm.invoice_due.post_due_interval_days
                  "
                  type="number"
                  min="1"
                  class="input-field w-40"
                />
                <p class="text-xs text-gray-500 mt-1">
                  Reminder sends every N overdue days (e.g., 3 = days 3, 6,
                  9...).
                </p>
              </div>

              <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label class="block text-sm font-medium text-gray-700 mb-2">
                    Max post-due reminders (optional)
                  </label>
                  <input
                    v-model.number="
                      remindersForm.invoice_due.post_due_max_reminders
                    "
                    type="number"
                    min="1"
                    class="input-field"
                    placeholder="Unlimited if empty"
                  />
                </div>
                <div>
                  <label class="block text-sm font-medium text-gray-700 mb-2">
                    Stop post-due after days (optional)
                  </label>
                  <input
                    v-model.number="
                      remindersForm.invoice_due.post_due_stop_after_days
                    "
                    type="number"
                    min="1"
                    class="input-field"
                    placeholder="Unlimited if empty"
                  />
                </div>
              </div>
            </div>
          </div>

          <div class="p-4 bg-gray-50 border border-gray-200 rounded-lg">
            <h3 class="font-medium text-gray-900 mb-2">Event Reminders</h3>
            <p class="text-sm text-gray-600">Coming soon.</p>
          </div>

          <div class="flex justify-end pt-4 border-t border-gray-200">
            <button
              @click="saveReminderSettings"
              :disabled="isSavingReminders"
              class="btn-primary"
            >
              <span v-if="isSavingReminders">Saving...</span>
              <span v-else>Save Changes</span>
            </button>
          </div>
        </div>
      </div>
    </div>
  </div>

  <!-- Bank Account Disconnect Confirmation Modal -->
  <div
    v-if="showDisconnectBankModal"
    class="fixed inset-0 z-50 overflow-y-auto"
  >
    <div
      class="flex items-end justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0"
    >
      <!-- Background overlay -->
      <div
        class="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity"
        @click="showDisconnectBankModal = false"
      ></div>

      <!-- Modal -->
      <div
        class="relative inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full"
      >
        <div class="bg-white px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
          <div class="sm:flex sm:items-start">
            <div
              class="mx-auto flex-shrink-0 flex items-center justify-center h-12 w-12 rounded-full bg-red-100 sm:mx-0 sm:h-10 sm:w-10"
            >
              <svg
                class="h-6 w-6 text-red-600"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  stroke-width="2"
                  d="M12 9v2m0 4v2m0 4v2M6.343 17.657a8 8 0 1111.314 0M12 5a1 1 0 110-2 1 1 0 010 2z"
                />
              </svg>
            </div>
            <div class="mt-3 text-center sm:mt-0 sm:ml-4 sm:text-left w-full">
              <h3
                class="text-lg leading-6 font-medium text-gray-900"
                id="modal-title"
              >
                Disconnect Bank Account?
              </h3>
              <div class="mt-2 space-y-3">
                <div v-if="bankAccountToDisconnect" class="space-y-2">
                  <p class="text-sm text-gray-700">
                    <strong>Account:</strong>
                    {{ bankAccountToDisconnect.account_name }}
                  </p>
                  <p class="text-sm text-gray-700">
                    <strong>Institution:</strong>
                    {{ bankAccountToDisconnect.institution_name }}
                  </p>
                  <p class="text-sm text-gray-700">
                    <strong>Account Number:</strong> ••••{{
                      bankAccountToDisconnect.account_mask
                    }}
                  </p>
                </div>
                <p class="text-sm text-gray-500">
                  This will permanently disconnect this bank account and stop
                  automatic transaction syncing.
                </p>
                <div class="p-3 bg-yellow-50 border border-yellow-200 rounded">
                  <p class="text-xs text-yellow-800">
                    <strong>Warning:</strong> After disconnection:
                  </p>
                  <ul
                    class="text-xs text-yellow-800 mt-2 space-y-1 list-disc list-inside"
                  >
                    <li>Transaction syncing will stop immediately</li>
                    <li>Historical transaction data will remain available</li>
                    <li>
                      You can reconnect this account or add a different one
                      later
                    </li>
                    <li>This action cannot be undone immediately</li>
                  </ul>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- Modal Actions -->
        <div
          class="bg-gray-50 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse gap-2"
        >
          <button
            @click="confirmDisconnectBank"
            :disabled="isDisconnectingBank"
            class="w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-red-600 text-base font-medium text-white hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 disabled:opacity-50 disabled:cursor-not-allowed sm:w-auto sm:text-sm transition-colors"
          >
            <span v-if="isDisconnectingBank">Disconnecting...</span>
            <span v-else>Yes, Disconnect</span>
          </button>
          <button
            @click="showDisconnectBankModal = false"
            :disabled="isDisconnectingBank"
            class="w-full inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary disabled:opacity-50 disabled:cursor-not-allowed sm:w-auto sm:text-sm transition-colors"
          >
            Cancel
          </button>
        </div>
      </div>
    </div>
  </div>

  <!-- Stripe Disconnect Confirmation Modal -->
  <div
    v-if="showDisconnectConfirmation"
    class="fixed inset-0 z-50 overflow-y-auto"
  >
    <div
      class="flex items-end justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0"
    >
      <!-- Background overlay -->
      <div
        class="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity"
        @click="showDisconnectConfirmation = false"
      ></div>

      <!-- Modal -->
      <div
        class="relative inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full"
      >
        <div class="bg-white px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
          <div class="sm:flex sm:items-start">
            <div
              class="mx-auto flex-shrink-0 flex items-center justify-center h-12 w-12 rounded-full bg-red-100 sm:mx-0 sm:h-10 sm:w-10"
            >
              <svg
                class="h-6 w-6 text-red-600"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  stroke-width="2"
                  d="M12 9v2m0 4v2m0 4v2M6.343 17.657a8 8 0 1111.314 0M12 5a1 1 0 110-2 1 1 0 010 2z"
                />
              </svg>
            </div>
            <div class="mt-3 text-center sm:mt-0 sm:ml-4 sm:text-left w-full">
              <h3
                class="text-lg leading-6 font-medium text-gray-900"
                id="modal-title"
              >
                Disconnect Stripe Account?
              </h3>
              <div class="mt-2 space-y-3">
                <p class="text-sm text-gray-500">
                  This will permanently disconnect your Stripe Express account
                  and clear all payment settings.
                </p>
                <div class="p-3 bg-yellow-50 border border-yellow-200 rounded">
                  <p class="text-xs text-yellow-800">
                    <strong>Warning:</strong> After disconnection:
                  </p>
                  <ul
                    class="text-xs text-yellow-800 mt-2 space-y-1 list-disc list-inside"
                  >
                    <li>Residents will not be able to pay invoices online</li>
                    <li>You can reconnect a different Stripe account later</li>
                    <li>This action cannot be undone immediately</li>
                  </ul>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- Modal Actions -->
        <div
          class="bg-gray-50 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse gap-2"
        >
          <button
            @click="disconnectStripe"
            :disabled="isDisconnecting"
            class="w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-red-600 text-base font-medium text-white hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 disabled:opacity-50 disabled:cursor-not-allowed sm:w-auto sm:text-sm transition-colors"
          >
            <span v-if="isDisconnecting">Disconnecting...</span>
            <span v-else>Yes, Disconnect</span>
          </button>
          <button
            @click="showDisconnectConfirmation = false"
            :disabled="isDisconnecting"
            class="w-full inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary disabled:opacity-50 disabled:cursor-not-allowed sm:w-auto sm:text-sm transition-colors"
          >
            Cancel
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, watch, onMounted, nextTick } from 'vue';
import { useRoute } from 'vue-router';
import { useQueryClient } from '@tanstack/vue-query';
import {
  useSettings,
  useUpdateTenantSettings,
  useUpdateUserProfile,
  useUpdateLocalization,
  useUpdateReminderSettings,
  useUpdateZelleSettings,
  useRemoveZelleQr,
  useUploadHoaLogo,
  useRemoveHoaLogo,
  useTenantLogoUrl,
  useTenantZelleQrUrl,
  settingsApi,
  settingsKeys,
  stripeApi,
  useVerifyStripeStatus,
  useDisconnectStripe,
  usePlaidLinkToken,
  useBankAccounts,
  useExchangeToken,
  useDisconnectBankAccount,
  useSyncAccount,
  type SettingsData,
} from '@neibrpay/api-client';
import { useAuthStore } from '../stores/auth';

// Auth store and route
const authStore = useAuthStore();
const route = useRoute();
const isResident = computed(() => authStore.isResident);

// ---------------- GOOGLE AUTOCOMPLETE FOR HOA ADDRESS ----------------

async function loadGoogleMapsScript() {
  return new Promise((resolve, reject) => {
    // Already loaded?
    if (window.google && window.google.maps) {
      resolve(true);
      return;
    }

    // Script exists but waiting
    const existingScript = document.getElementById('google-maps-script');
    if (existingScript) {
      existingScript.addEventListener('load', resolve);
      return;
    }

    // Create script tag
    const script = document.createElement('script');
    script.id = 'google-maps-script';
    script.src = `https://maps.googleapis.com/maps/api/js?key=${
      import.meta.env.VITE_GOOGLE_MAPS_API_KEY
    }&libraries=places`;
    script.async = true;

    script.onload = resolve;
    script.onerror = reject;

    document.head.appendChild(script);
  });
}

async function initHoaAddressAutocomplete() {
  try {
    await google.maps.importLibrary('places');

    const input = document.getElementById('hoa-address') as HTMLInputElement;
    if (!input) return;

    const autocomplete = new google.maps.places.Autocomplete(input, {
      fields: ['formatted_address'],
      componentRestrictions: { country: ['us'] },
    });

    autocomplete.addListener('place_changed', () => {
      const place = autocomplete.getPlace();
      if (!place?.formatted_address) return;

      hoaForm.value.address = place.formatted_address;

      console.log('HOA Address Autocomplete:', place.formatted_address);
    });
  } catch (err) {
    console.error('HOA address autocomplete error:', err);
  }
}

// Tab state - initialize from hash if present
const validTabs = [
  'hoa',
  'user',
  'localization',
  'payments',
  'reminders',
  'bank',
] as const;
type TabType = (typeof validTabs)[number];

const getInitialTab = (): TabType => {
  // Check hash first (e.g., #payments or #payments?stripe_connect=success)
  const hashWithParams = window.location.hash.slice(1);
  if (hashWithParams) {
    // Extract just the tab name part before any query params
    const tabName = hashWithParams.split('?')[0];
    if (tabName && validTabs.includes(tabName as TabType)) {
      return tabName as TabType;
    }
  }

  // Fallback to query param for backwards compatibility
  const tabParam = route.query.tab as string;
  if (tabParam && validTabs.includes(tabParam as TabType)) {
    return tabParam as TabType;
  }

  return 'hoa';
};

const activeTab = ref<TabType>(getInitialTab());

// Update URL hash when active tab changes (preserve any query params)
watch(activeTab, (newTab: TabType) => {
  const currentHashWithParams = window.location.hash.slice(1);
  const currentParams = currentHashWithParams.includes('?')
    ? currentHashWithParams.split('?')[1]
    : '';

  window.location.hash = currentParams ? `${newTab}?${currentParams}` : newTab;
});

// Redirect to 'user' tab if resident tries to access admin-only tabs (including HOA)
watch(
  [isResident, activeTab],
  ([isResidentValue, currentTab]: [
    boolean,
    'hoa' | 'user' | 'localization' | 'payments' | 'reminders' | 'bank',
  ]) => {
    if (
      isResidentValue &&
      (currentTab === 'hoa' ||
        currentTab === 'localization' ||
        currentTab === 'payments' ||
        currentTab === 'reminders' ||
        currentTab === 'bank')
    ) {
      activeTab.value = 'user';
    }
  },
  { immediate: true }
);

// Success/Error messages
const showSuccessMessage = ref(false);
const showErrorMessage = ref(false);
const successMessage = ref('');
const errorMessage = ref('');

// Form data
const hoaForm = ref({
  name: '',
  address: '',
  phone: '',
});

const userForm = ref({
  name: '',
  email: '',
  phone_number: '',
});

const localizationForm = ref({
  currency: 'USD',
  currency_format: '$#,##0.00',
  timezone: 'UTC',
  date_format: 'MM/DD/YYYY',
  first_month_of_year: 'January',
});

const zelleForm = ref({
  zelle_enabled: false,
  zelle_email: '' as string | null,
  zelle_phone: '' as string | null,
  zelle_instructions: '' as string | null,
});
const remindersForm = ref({
  invoice_due: {
    enabled: true,
    pre_due_offsets_days: [30, 15, 7, 3, 2, 1] as number[],
    post_due_interval_days: 3,
    post_due_max_reminders: null as number | null,
    post_due_stop_after_days: null as number | null,
  },
  events: {
    enabled: false,
  },
});
const zelleQrInputRef = ref<HTMLInputElement | null>(null);

// Months array
const months = [
  'January',
  'February',
  'March',
  'April',
  'May',
  'June',
  'July',
  'August',
  'September',
  'October',
  'November',
  'December',
];

// Query and mutations
const queryClient = useQueryClient();
const { data: settingsData, isLoading } = useSettings();
const { logoUrl: tenantLogoUrl } = useTenantLogoUrl(settingsData);
const { zelleQrUrl: tenantZelleQrUrl } = useTenantZelleQrUrl(settingsData);
const updateTenantMutation = useUpdateTenantSettings();
const updateUserMutation = useUpdateUserProfile();
const updateLocalizationMutation = useUpdateLocalization();
const updateReminderMutation = useUpdateReminderSettings();
const updateZelleMutation = useUpdateZelleSettings();
const removeZelleQrMutation = useRemoveZelleQr();
const uploadHoaLogoMutation = useUploadHoaLogo();
const removeHoaLogoMutation = useRemoveHoaLogo();

// Loading states
const isSavingHoa = computed(() => updateTenantMutation.isPending.value);
const isSavingUser = computed(() => updateUserMutation.isPending.value);
const isSavingLocalization = computed(
  () => updateLocalizationMutation.isPending.value
);
const isSavingReminders = computed(
  () => updateReminderMutation.isPending.value
);
const isSavingZelle = computed(() => updateZelleMutation.isPending.value);
const isRemovingZelleQr = computed(() => removeZelleQrMutation.isPending.value);

const zelleQrUrl = computed(() => tenantZelleQrUrl.value ?? null);
const isUploadingZelleQr = ref(false);

const hoaLogoUrl = computed(() => tenantLogoUrl.value ?? null);
const hoaLogoInputRef = ref<HTMLInputElement | null>(null);
const isUploadingHoaLogo = computed(
  () => uploadHoaLogoMutation.isPending.value
);
const isRemovingHoaLogo = computed(() => removeHoaLogoMutation.isPending.value);

// Watch for settings data and populate forms
watch(
  settingsData,
  (data: SettingsData | undefined) => {
    if (data) {
      // Populate HOA form only for non-residents (residents cannot see or update HOA)
      if (!isResident.value) {
        hoaForm.value = {
          name: data.tenant.name || '',
          address: data.tenant.address || '',
          phone: data.tenant.phone || '',
        };
      }

      // Populate user form
      userForm.value = {
        name: data.user.name || '',
        email: data.user.email || '',
        phone_number: data.user.phone_number || '',
      };

      // Populate localization form
      localizationForm.value = {
        currency: data.tenant.settings.currency || 'USD',
        currency_format: data.tenant.settings.currency_format || '$#,##0.00',
        timezone: data.tenant.settings.timezone || 'UTC',
        date_format: data.tenant.settings.date_format || 'MM/DD/YYYY',
        first_month_of_year:
          data.tenant.settings.first_month_of_year || 'January',
      };

      zelleForm.value = {
        zelle_enabled: data.tenant.settings.zelle_enabled ?? false,
        zelle_email: data.tenant.settings.zelle_email ?? null,
        zelle_phone: data.tenant.settings.zelle_phone ?? null,
        zelle_instructions: data.tenant.settings.zelle_instructions ?? null,
      };

      const invoiceDue = data.tenant.settings.reminders?.invoice_due;
      remindersForm.value = {
        invoice_due: {
          enabled: invoiceDue?.enabled ?? true,
          pre_due_offsets_days: invoiceDue?.pre_due_offsets_days?.length
            ? [...invoiceDue.pre_due_offsets_days]
            : [30, 15, 7, 3, 2, 1],
          post_due_interval_days: invoiceDue?.post_due_interval_days ?? 3,
          post_due_max_reminders: invoiceDue?.post_due_max_reminders ?? null,
          post_due_stop_after_days:
            invoiceDue?.post_due_stop_after_days ?? null,
        },
        events: {
          enabled: data.tenant.settings.reminders?.events?.enabled ?? false,
        },
      };
    }
  },
  { immediate: true }
);

onMounted(async () => {
  await loadGoogleMapsScript();

  if (!isResident.value && activeTab.value === 'hoa') {
    await nextTick();
    initHoaAddressAutocomplete();
  }

  // Listen for hash changes (back/forward buttons)
  window.addEventListener('hashchange', () => {
    const hashWithParams = window.location.hash.slice(1);
    if (hashWithParams) {
      // Extract just the tab name part before any query params
      const tabName = hashWithParams.split('?')[0];
      if (tabName && validTabs.includes(tabName as TabType)) {
        activeTab.value = tabName as TabType;
      }
    }
  });
});

watch(activeTab, async (tab: TabType) => {
  if (!isResident.value && tab === 'hoa') {
    await nextTick();
    initHoaAddressAutocomplete();
  }
});

// Helper functions
const showSuccess = (message: string) => {
  successMessage.value = message;
  showSuccessMessage.value = true;
  setTimeout(() => {
    showSuccessMessage.value = false;
  }, 5000);
};

const showError = (message: string) => {
  errorMessage.value = message;
  showErrorMessage.value = true;
  setTimeout(() => {
    showErrorMessage.value = false;
  }, 5000);
};

// Save functions
const saveHoaSettings = async () => {
  try {
    await updateTenantMutation.mutateAsync(hoaForm.value);
    showSuccess('HOA settings updated successfully');
  } catch (error: any) {
    showError(error.message || 'Failed to update HOA settings');
  }
};

const saveUserSettings = async () => {
  try {
    await updateUserMutation.mutateAsync(userForm.value);
    showSuccess('User profile updated successfully');
  } catch (error: any) {
    showError(error.message || 'Failed to update user profile');
  }
};

const saveLocalizationSettings = async () => {
  try {
    await updateLocalizationMutation.mutateAsync(localizationForm.value);
    showSuccess('Localization settings updated successfully');
  } catch (error: any) {
    showError(error.message || 'Failed to update localization settings');
  }
};

const saveReminderSettings = async () => {
  try {
    const uniqueOffsets: number[] = Array.from(
      new Set<number>(
        remindersForm.value.invoice_due.pre_due_offsets_days
          .map((v: number) => Number(v))
          .filter((v: number) => Number.isInteger(v) && v > 0)
      )
    ).sort((a: number, b: number) => b - a);

    await updateReminderMutation.mutateAsync({
      invoice_due: {
        enabled: remindersForm.value.invoice_due.enabled,
        pre_due_offsets_days: uniqueOffsets,
        post_due_interval_days: Math.max(
          1,
          Number(remindersForm.value.invoice_due.post_due_interval_days || 1)
        ),
        post_due_max_reminders:
          remindersForm.value.invoice_due.post_due_max_reminders || null,
        post_due_stop_after_days:
          remindersForm.value.invoice_due.post_due_stop_after_days || null,
      },
      events: {
        enabled: remindersForm.value.events.enabled,
      },
    });

    remindersForm.value.invoice_due.pre_due_offsets_days = uniqueOffsets;
    showSuccess('Reminder settings updated successfully');
  } catch (error: any) {
    showError(error.message || 'Failed to update reminder settings');
  }
};

const addPreDueOffset = () => {
  remindersForm.value.invoice_due.pre_due_offsets_days.push(1);
};

const removePreDueOffset = (index: number) => {
  remindersForm.value.invoice_due.pre_due_offsets_days.splice(index, 1);
};

const saveZelleSettings = async () => {
  try {
    await updateZelleMutation.mutateAsync({
      zelle_enabled: Boolean(zelleForm.value.zelle_enabled),
      zelle_email: zelleForm.value.zelle_email || null,
      zelle_phone: zelleForm.value.zelle_phone || null,
      zelle_instructions: zelleForm.value.zelle_instructions || null,
    });
    showSuccess('Zelle settings saved');
  } catch (error: any) {
    showError(error.message || 'Failed to save Zelle settings');
  }
};

const onZelleQrFileChange = async (e: Event) => {
  const input = e.target as HTMLInputElement;
  const file = input.files?.[0];
  if (!file) return;
  isUploadingZelleQr.value = true;
  try {
    await settingsApi.uploadZelleQr(file);
    showSuccess('QR code uploaded');
    queryClient.invalidateQueries({ queryKey: settingsKeys.detail() });
    queryClient.invalidateQueries({ queryKey: settingsKeys.zelleQrUrl() });
  } catch (error: any) {
    showError(error.message || 'Failed to upload QR code');
  } finally {
    isUploadingZelleQr.value = false;
    input.value = '';
  }
};

const removeZelleQr = async () => {
  try {
    await removeZelleQrMutation.mutateAsync();
    showSuccess('QR code removed');
  } catch (error: any) {
    showError(error.message || 'Failed to remove QR code');
  }
};

const triggerZelleQrInput = () => {
  const el = zelleQrInputRef.value;
  if (el && 'click' in el) (el as HTMLInputElement).click();
};

const onHoaLogoFileChange = async (e: Event) => {
  const input = e.target as HTMLInputElement;
  const file = input.files?.[0];
  if (!file) return;
  try {
    await uploadHoaLogoMutation.mutateAsync(file);
    showSuccess('Logo uploaded');
  } catch (error: unknown) {
    showError(error instanceof Error ? error.message : 'Failed to upload logo');
  } finally {
    input.value = '';
  }
};

const removeHoaLogo = async () => {
  try {
    await removeHoaLogoMutation.mutateAsync();
    showSuccess('Logo removed');
  } catch (error: unknown) {
    showError(error instanceof Error ? error.message : 'Failed to remove logo');
  }
};

const triggerHoaLogoInput = () => {
  const el = hoaLogoInputRef.value;
  if (el?.click) el.click();
};

// ============ STRIPE PAYMENTS SECTION ============

// Stripe mutations and queries
const verifyStripeMutation = useVerifyStripeStatus();
const disconnectStripeMutation = useDisconnectStripe();

// Stripe state
const stripeConnectId = computed(
  () => settingsData.value?.tenant?.settings?.stripe_connect_id || null
);
const stripeConnectStatus = computed(
  () =>
    settingsData.value?.tenant?.settings?.stripe_connect_status ||
    'not_connected'
);
const chargesEnabled = computed(
  () => settingsData.value?.tenant?.settings?.charges_enabled || false
);

// Stripe loading states
const isConnectingStripe = ref(false);
const isLoadingDashboard = ref(false);
const isVerifyingStatus = computed(() => verifyStripeMutation.isPending.value);
const isDisconnecting = computed(
  () => disconnectStripeMutation.isPending.value
);
const showDisconnectConfirmation = ref(false);

/**
 * Initiate Stripe Connect account creation
 */
const connectStripe = async () => {
  isConnectingStripe.value = true;
  try {
    const response = await stripeApi.connect();
    if (response.onboarding_url) {
      // Redirect to Stripe onboarding
      window.location.href = response.onboarding_url;
    } else {
      showError('Failed to get onboarding URL');
    }
  } catch (error: any) {
    showError(error.message || 'Failed to connect Stripe account');
  } finally {
    isConnectingStripe.value = false;
  }
};

/**
 * Open Stripe Express dashboard
 */
const openStripeDashboard = async () => {
  isLoadingDashboard.value = true;
  try {
    const response = await stripeApi.getDashboardLink();
    if (response.dashboard_url) {
      window.open(response.dashboard_url, '_blank');
    } else {
      showError('Failed to get dashboard link');
    }
  } catch (error: any) {
    showError(error.message || 'Failed to load Stripe dashboard');
  } finally {
    isLoadingDashboard.value = false;
  }
};

/**
 * Verify Stripe account status
 */
const verifyStripeStatus = async () => {
  try {
    await verifyStripeMutation.mutateAsync();
    showSuccess('Account status updated');
  } catch (error: any) {
    showError(error.message || 'Failed to verify account status');
  }
};

/**
 * Disconnect Stripe account
 */
const disconnectStripe = async () => {
  try {
    await disconnectStripeMutation.mutateAsync();
    showSuccess(
      'Stripe account disconnected successfully. You can now connect a new account.'
    );
    showDisconnectConfirmation.value = false;
  } catch (error: any) {
    showError(error.message || 'Failed to disconnect Stripe account');
  }
};

/**
 * Check if returning from Stripe onboarding (on mount)
 */
const checkStripeReturn = () => {
  const params = new URLSearchParams(window.location.search);
  if (params.get('stripe_connect') === 'success') {
    showSuccess('Stripe account connected! Verifying status...');
    // Verify status immediately
    verifyStripeStatus();
    // Clean up URL
    window.history.replaceState({}, document.title, window.location.pathname);
  } else if (params.get('stripe_connect') === 'refresh') {
    showError('Stripe onboarding was refreshed. Please try again.');
    window.history.replaceState({}, document.title, window.location.pathname);
  }
};

// Check for Stripe return on mount
import { onBeforeMount } from 'vue';
onBeforeMount(() => {
  checkStripeReturn();
});

// ============ PLAID BANK INTEGRATION ============

// Plaid queries and mutations
const { data: linkTokenData, isLoading: isLoadingLinkToken } =
  usePlaidLinkToken();
const { data: bankAccountsData } = useBankAccounts();
const exchangeTokenMutation = useExchangeToken();
const disconnectBankMutation = useDisconnectBankAccount();
const syncAccountMutation = useSyncAccount();

// Bank account list
const bankAccounts = computed(
  () => bankAccountsData.value?.bank_accounts || []
);

// Plaid loading states
const isConnectingBank = ref(false);
const isDisconnectingBank = ref(false);
const isManualSyncing = ref(false);
const showDisconnectBankModal = ref(false);
const bankAccountToDisconnect = ref<{
  id: number;
  account_name: string;
  institution_name: string;
  account_mask: string;
} | null>(null);

// Format date helper
const formatDate = (dateString: string | null) => {
  if (!dateString) return 'Never';
  try {
    return new Date(dateString).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  } catch {
    return 'Invalid date';
  }
};

/**
 * Load Plaid Link script via fetch and eval (bypasses CDN 403)
 */
const loadPlaidScript = async (): Promise<void> => {
  return new Promise((resolve, reject) => {
    // Check if already loaded
    if ((window as any).Plaid) {
      resolve();
      return;
    }

    // Try loading script via standard method first
    const script = document.createElement('script');
    script.id = 'plaid-link-script';
    script.src = 'https://cdn.plaid.com/link/v2/stable/link-initialize.js';

    script.onload = () => {
      console.log('Plaid script loaded successfully');
      resolve();
    };

    script.onerror = e => {
      console.error('Plaid script load error:', e);
      // Try alternative URL
      const altScript = document.createElement('script');
      altScript.src = 'https://plaid.com/link/v2/stable/link-initialize.js';
      altScript.onload = () => {
        console.log('Plaid script loaded via alternative URL');
        resolve();
      };
      altScript.onerror = () => {
        reject(
          new Error(
            'Failed to load Plaid Link. Please try using a different browser or disable ad blockers.'
          )
        );
      };
      document.head.appendChild(altScript);
    };

    document.head.appendChild(script);
  });
};

/**
 * Initialize Plaid Link
 */
const connectBank = async () => {
  // First, fetch link token if not already loaded
  if (!linkTokenData.value?.link_token) {
    showError('Loading bank connection...');
    await new Promise(resolve => setTimeout(resolve, 1500));

    if (!linkTokenData.value?.link_token) {
      showError(
        'Failed to initialize bank connection. Please refresh and try again.'
      );
      return;
    }
  }

  isConnectingBank.value = true;

  try {
    // Load Plaid script
    await loadPlaidScript();

    const linkToken = linkTokenData.value!.link_token;

    // Create and open handler
    const handler = (window as any).Plaid.create({
      token: linkToken,
      onSuccess: async (publicToken: string, metadata: any) => {
        console.log('Plaid Link Success:', metadata);
        try {
          showSuccess('Connecting bank account...');
          await exchangeTokenMutation.mutateAsync({
            public_token: publicToken,
          });
          showSuccess('Bank account connected successfully!');
        } catch (error: any) {
          console.error('Exchange token error:', error);
          showError(error.message || 'Failed to connect bank account');
        } finally {
          isConnectingBank.value = false;
        }
      },
      onExit: (err: any, metadata: any) => {
        console.log('Plaid Link Exit:', { err, metadata });
        isConnectingBank.value = false;
        if (err) {
          showError(
            `Bank connection closed: ${err.display_message || err.error_message || 'User cancelled'}`
          );
        }
      },
      onEvent: (eventName: string, metadata: any) => {
        console.log('Plaid Event:', eventName, metadata);
      },
    });

    handler.open();
  } catch (error: any) {
    console.error('Connect bank error:', error);
    isConnectingBank.value = false;
    showError(
      error.message ||
        'Failed to open bank connection. Please try in a different browser.'
    );
  }
};

/**
 * Open bank account disconnect modal
 */
const openDisconnectBankModal = (account: {
  id: number;
  account_name: string;
  institution_name: string;
  account_mask: string;
}) => {
  bankAccountToDisconnect.value = account;
  showDisconnectBankModal.value = true;
};

/**
 * Confirm and disconnect a bank account
 */
const confirmDisconnectBank = async () => {
  if (!bankAccountToDisconnect.value) {
    return;
  }

  const accountId = bankAccountToDisconnect.value.id;
  isDisconnectingBank.value = true;
  try {
    await disconnectBankMutation.mutateAsync(accountId);
    showSuccess('Bank account disconnected successfully');
    showDisconnectBankModal.value = false;
    bankAccountToDisconnect.value = null;
  } catch (error: any) {
    showError(error.message || 'Failed to disconnect bank account');
  } finally {
    isDisconnectingBank.value = false;
  }
};

/**
 * Manually sync all bank accounts
 * Continues syncing even if one account fails
 */
const manualSyncBankAccounts = async () => {
  isManualSyncing.value = true;
  const results = {
    success: 0,
    failed: 0,
    errors: [] as string[],
  };

  try {
    // Sync all accounts, continue even if one fails
    for (const account of bankAccounts.value) {
      try {
        await syncAccountMutation.mutateAsync({
          bank_account_id: account.id,
        });
        results.success++;
      } catch (error: any) {
        results.failed++;
        results.errors.push(
          `${account.account_name}: ${error.message || 'Sync failed'}`
        );
        console.error(`Failed to sync account ${account.id}:`, error);
      }
    }

    // Show appropriate message based on results
    if (results.success === bankAccounts.value.length) {
      showSuccess('All bank accounts synced successfully');
    } else if (results.success > 0) {
      showSuccess(
        `Synced ${results.success} account(s). ${results.failed} failed.`
      );
      if (results.errors.length > 0) {
        console.warn('Sync errors:', results.errors);
      }
    } else {
      showError(`Failed to sync all accounts. ${results.errors.join('; ')}`);
    }
  } catch (error: any) {
    showError(error.message || 'Failed to sync bank accounts');
  } finally {
    isManualSyncing.value = false;
  }
};
</script>
